<template>
  <div class="home">
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-alert v-if="submittedFeedback" border="top" type="success" class="my-2">
            Feedback Submitted!
            <br />
            Thank you for your feedback. We value our customer's opinion and will do all we can to
            meet (and exceed) your expectations.
          </v-alert>

          <template v-if="givingFeedback && flingStatus === 'loaded'">
            <v-row>
              <v-col cols="12" class="d-flex justify-center">
                <v-card max-width="500" width="500" class="pa-5">
                  <v-form>
                    <v-text-field
                      label="To"
                      required
                      readonly
                      outlined
                      value="Faithful Fling's Customer Support"
                    />

                    <v-text-field
                      label="From"
                      required
                      readonly
                      outlined
                      hide-details
                      :value="anonymous ? 'A valued customer' : userData.email"
                    />

                    <v-checkbox
                      v-model="anonymous"
                      label="I would like to be anonymous"
                      class="mt-0"
                    />

                    <v-text-field label="About" required readonly outlined :value="fling.title" />

                    <v-textarea
                      label="Feedback"
                      placeholder="Please let us know any feedback you have."
                      v-model="feedback"
                      outlined
                      persistent-placeholder
                      required
                    />

                    <div class="text-right">
                      <v-btn class="mr-2" @click.prevent="resetFeedback" plain> cancel </v-btn>

                      <v-btn
                        color="primary"
                        :disabled="submittingFeedback || feedback === ''"
                        @click.prevent="submitFeedback"
                      >
                        Submit
                        <v-progress-circular
                          v-if="submittingFeedback"
                          size="20"
                          width="2"
                          indeterminate
                          class="ml-2"
                        />
                      </v-btn>
                    </div>
                  </v-form>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-else-if="changingNames && flingStatus === 'loaded'">
            <v-row>
              <v-col cols="12" class="d-flex justify-center">
                <v-card max-width="500" width="500" class="pa-5">
                  <v-form>
                    <v-text-field label="Fling" required readonly outlined :value="fling.title" />

                    <v-text-field label="Male Name" required outlined v-model="male_name" />

                    <v-text-field label="Female Name" required outlined v-model="female_name" />

                    <div class="text-right">
                      <v-btn class="mr-2" @click.prevent="changingNames = false" plain>
                        cancel
                      </v-btn>

                      <v-btn
                        color="primary"
                        :disabled="submittingNames || male_name === '' || female_name === ''"
                        @click.prevent="submitNames"
                      >
                        Reload
                        <v-progress-circular
                          v-if="submittingNames"
                          size="20"
                          width="2"
                          indeterminate
                          class="ml-2"
                        />
                      </v-btn>
                    </div>
                  </v-form>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-else-if="flingStatus === 'loaded'">
            <div class="d-flex justify-space-between mb-3">
              <div>
                <v-btn
                  color="light"
                  :ripple="false"
                  class=""
                  @click="$router.push({ name: 'home' })"
                >
                  Back
                </v-btn>
              </div>

              <div>
                <v-btn color="primary" block @click.prevent="setupChangingNames">
                  Change names
                </v-btn>
              </div>
            </div>

            <v-card class="pa-5">
              <div>
                <div class="text-secondary xl mb-3 text-center">
                  {{ fling.title }}
                </div>

                <div class="d-flex justify-space-around my-5">
                  <div v-if="fling.setup_time !== null" class="d-flex flex-column text-center">
                    <div class="text-uppercase text-secondary sm font-weight-bold">Setup Time</div>
                    <div>
                      <v-icon small> mdi-clock-time-three-outline </v-icon>
                      {{ fling.setup_time }}
                    </div>
                  </div>

                  <div v-if="fling.setup_time !== null" class="d-flex flex-column text-center">
                    <div class="text-uppercase text-secondary sm font-weight-bold">
                      Complete Time
                    </div>
                    <div>
                      <v-icon small> mdi-timer-sand-empty </v-icon>
                      {{ fling.complete_time }}
                    </div>
                  </div>

                  <div class="d-flex flex-column text-center">
                    <div class="text-uppercase text-secondary sm font-weight-bold">Completed</div>
                    <div
                      role="button"
                      @click.prevent="fling.completed ? markAsIncomplete() : markAsComplete()"
                    >
                      <v-icon v-if="!fling.completed"> mdi-checkbox-blank-circle-outline </v-icon>
                      <v-icon v-else color="primary"> mdi-check-circle-outline </v-icon>
                    </div>
                  </div>
                </div>

                <div v-html="fling.fling_content" />
              </div>

              <v-row class="mt-5 justify-end">
                <v-col cols="12" md="3" xl="2" class="py-2">
                  <v-btn color="primary" block @click.prevent="givingFeedback = true">
                    Give feedback
                  </v-btn>
                </v-col>
                <v-col cols="12" md="4" xl="2" class="py-2">
                  <v-btn
                    color="primary"
                    block
                    @click.prevent="fling.completed ? markAsIncomplete() : markAsComplete()"
                  >
                    Mark as {{ fling.completed ? "incomplete" : "complete" }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </template>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: "FlingView",
  props: {
    flingId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      fling: {},
      flingStatus: "loading",
      givingFeedback: false,
      feedback: "",
      submittingFeedback: false,
      submittedFeedback: false,
      anonymous: false,
      changingNames: false,
      male_name: "",
      female_name: "",
      submittingNames: false,
      submittingNamesErrors: false,
    };
  },
  created() {
    this.fetchFling();
  },
  methods: {
    async fetchFling() {
      this.flingStatus = "loading";

      const responseData = await this.api({
        url: `users/${this.userData.id}/fling/?fling_id=${this.flingId}`,
      });

      if (responseData.status === 200) {
        this.fling = responseData.body;
        this.flingStatus = "loaded";
      } else {
        this.flingStatus = "error";
      }
    },
    markAsComplete() {
      this.api({
        url: `users/${this.userData.id}/mark_as_complete/`,
        method: "POST",
        params: { fling_id: this.fling.id },
      });

      this.fling.completed = true;
    },
    markAsIncomplete() {
      this.api({
        url: `users/${this.userData.id}/mark_as_incomplete/`,
        method: "POST",
        params: { fling_id: this.fling.id },
      });

      this.fling.completed = false;
    },
    submitFeedback() {
      this.submittingFeedback = true;

      this.api({
        url: `users/${this.userData.id}/feedback/`,
        method: "POST",
        json: {
          fling_id: this.fling.id,
          feedback: this.feedback,
          anonymous: this.anonymous,
        },
      });

      this.submittedFeedback = true;

      this.resetFeedback();
    },
    resetFeedback() {
      this.givingFeedback = false;
      this.feedback = "";
      this.submittingFeedback = false;
      this.anonymous = false;
    },
    setupChangingNames() {
      this.changingNames = true;
      this.male_name = this.fling.his_name;
      this.female_name = this.fling.her_name;
    },
    submitNames() {
      this.submittingNames = true;

      this.api({
        url: `users/${this.userData.id}/change_names/`,
        method: "POST",
        json: {
          fling_id: this.fling.id,
          male_name: this.male_name,
          female_name: this.female_name,
        },
      });

      // eslint-disable-next-line
      location.reload();
    },
  },
};
</script>
